import React, { useEffect, useRef, useState } from 'react';
import { defaultLayoutIcons, DefaultVideoLayout } from '@vidstack/react/player/layouts/default';
import { MediaPlayer, MediaProvider, Poster, useMediaState } from '@vidstack/react';
import IconPlay from '$partials/icons/icon-play.jsx';
import IconGallery from '$partials/icons/icon-gallery.jsx';
/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/media.d.ts').GalleryVideo}
 */
const GalleryVideo = ({
    height,
    onFullscreenChange,
    onPlayingStateChange,
    playButtonPosition = 'bottom-left',
    poster,
    src,
    cursorVisible = true,
    title = '',
    width,
}) => {
    const playerRef = useRef(null);
    const cursorRef = useRef(null);
    const playButtonRef = useRef(null);

    const startPlay = () => {
        playerRef.current.play?.();
    };
    const isStarted = useMediaState('started', playerRef);
    const isPlaying = useMediaState('playing', playerRef);

    useEffect(() => {
        if (onPlayingStateChange) {
            onPlayingStateChange(isPlaying);
        }
    }, [isPlaying, onPlayingStateChange]);

    const [cursorPos, setCursorPos] = useState({ x: 0,
        y: 0,
        visible: false });

    const handleMouseMove = (e) => {
        const rect = cursorRef.current?.getBoundingClientRect();
        if (!rect) { return; }
        // position the 40x40 box so its bottom-right corner is at the cursor
        const x = e.clientX - rect.left - 40;
        const y = e.clientY - rect.top - 40;

        // hide cursor-icon when inside an expanded area around the play button
        const btnRect = playButtonRef.current?.getBoundingClientRect();
        let inPlayArea = false;
        if (btnRect) {
            const pad = 12; // pixels of extra clickable area around button
            const left = btnRect.left - pad;
            const right = btnRect.right + pad;
            const top = btnRect.top - pad;
            const bottom = btnRect.bottom + pad;
            if (e.clientX >= left && e.clientX <= right && e.clientY >= top && e.clientY <= bottom) {
                inPlayArea = true;
            }
        }
        setCursorPos({ x,
            y,
            visible: !inPlayArea });
    };

    const handleMouseLeave = () => setCursorPos((s) => ({ ...s,
        visible: false }));
    return (
        <MediaPlayer
            crossOrigin
            playsInline
            aspectRatio={width && height ? `${width}/${height}` : '16/9'}
            load='play'
            onFocus={() => {
                playerRef.current.controls?.show?.();
            }}
            onFullscreenChange={onFullscreenChange}
            posterLoad='eager'
            ref={playerRef}
            src={src}
            title={title}>
            <div
                className='w-full h-full'
                onMouseEnter={() => setCursorPos((s) => ({ ...s,
                    visible: true }))}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                ref={cursorRef}>

                <MediaProvider>
                    {poster && (
                        <Poster
                            alt='GalleryVideo placeholder image'
                            className={`vds-poster ${isStarted ? 'hidden' : ''}`}
                            src={poster} />
                    )}
                    {!isStarted && (
                        <>

                            <div className={`absolute z-50
                                ${playButtonPosition === 'bottom-left' ? 'left-5 bottom-5' : ''}
                                ${playButtonPosition === 'center' ? 'left-0 right-0 text-center' : ''}
                            `}>
                                <button
                                    className='px-10 py-5 inline-flex items-center type-style-7 bg-white h-40 w-40 rounded bg-grey-grey-200 justify-center'
                                    onClick={(e) => { e.stopPropagation(); startPlay(); }}
                                    ref={playButtonRef}
                                    type='button'>
                                    <IconPlay />
                                </button>
                            </div>
                            {/* Cursor-following grey box (40x40) */}
                            <div
                                aria-hidden
                                className='hidden md:flex absolute left-0 top-0 h-40 w-40 rounded bg-grey-grey-200 pointer-events-none items-center justify-center'
                                style={{
                                    transform: `translate(${cursorPos.x}px, ${cursorPos.y}px)`,
                                    transition: 'transform 0.04s linear, opacity 0.08s linear',
                                    opacity: (cursorPos.visible && cursorVisible) ? 1 : 0,
                                    zIndex: 40,
                                }}>
                                <IconGallery fill='#2c2b2aff'/>
                            </div>
                        </>
                    )}
                </MediaProvider>
            </div>
            {isStarted && (
                <DefaultVideoLayout
                    icons={defaultLayoutIcons}
                    slots={{
                        title: null,
                        chapterTitle: null,
                        airPlayButton: null,
                        googleCastButton: null
                    }} />
            )}
        </MediaPlayer>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

export default GalleryVideo;
